/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { UmssSelectComponent, SelectOption } from '../select/select.component';
import { ShiftStatusEnum, SHIFT_STATUS_RECORD } from '@umss/shared/utils';
import { UmssButtonGroupComponent } from '../button-group/button-group.component';
import { UmssToggleButtonComponent } from '../toggle-button/toggle-button.component';

@Component({
  selector: 'umss-shift-status-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UmssSelectComponent,
    UmssButtonGroupComponent,
    UmssToggleButtonComponent
  ],
  template: `
    @if (displayMode === 'select') {
      <umss-select
        [options]="options"
        [placeholder]="placeholder"
        [label]="showLabel ? label : undefined"
        [helperText]="helperText"
        [errorMessage]="errorMessage"
        [size]="size"
        [disabled]="disabled"
        [required]="required"
        [error]="error"
        [loading]="{ selector: loading }"
        [multiple]="multiple"
        [hideSelectedOptions]="hideSelectedOptions"
        [(value)]="value"
        [clearable]="clearable"
        (selectionChange)="onSelectionChange($event)"
        (onFocus)="onFocus()"
        (onBlur)="onBlur()"
      />
    } @else {
      <umss-button-group
        [formControl]="internalControl"
        [label]="showLabel ? label : undefined"
        [size]="size"
        [variant]="variant"
        [disabled]="disabled"
        [required]="required"
        [allowWrap]="true"
        [multiple]="multiple"
        [clearable]="clearable"
        [maxPerRow]="maxPerRow"
        [fillLastRow]="fillLastRow"
      >
        @for (option of options; track option.value) {
          <umss-toggle-button [label]="option.label" [value]="option.value" />
        }
      </umss-button-group>
    }
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssShiftStatusSelectComponent),
      multi: true
    }
  ]
})
export class UmssShiftStatusSelectComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() displayMode: 'select' | 'button-group' = 'select';
  @Input() showLabel: boolean = false;
  @Input() label?: string = 'Shift Status';
  @Input() placeholder: string = 'Select a shift status';
  @Input() helperText?: string;
  @Input() errorMessage?: string;
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() disabled = false;
  @Input() required = false;
  @Input() error = false;
  @Input() loading = false;
  @Input() multiple: boolean = false;
  @Input() clearable: boolean = true;
  @Input() variant: 'outlined' | 'ghost' = 'outlined';
  @Input() maxPerRow?: number;
  @Input() hideSelectedOptions: boolean = false;
  @Input() fillLastRow = false;
  @Input() set excludeStatuses(
    values: (ShiftStatusEnum | `${ShiftStatusEnum}`)[]
  ) {
    this._excludeStatuses = values.map((value) => value as ShiftStatusEnum);
    this.initializeOptions();
  }

  @Input() set value(val: ShiftStatusEnum | ShiftStatusEnum[] | null) {
    this._value = val;
  }
  get value(): ShiftStatusEnum | ShiftStatusEnum[] | null {
    return this._value;
  }

  @Output() valueChange = new EventEmitter<
    ShiftStatusEnum | ShiftStatusEnum[] | null
  >();
  @Output() selectionChange = new EventEmitter<
    ShiftStatusEnum | ShiftStatusEnum[] | null
  >();

  options: SelectOption[] = [];
  /**
   * Internal form control passed directly to umss-button-group when in
   * button-group mode.  This ensures the button-group receives a proper
   * writeValue() call so it can initialise its own pressed state — avoiding
   * the race condition where ngAfterContentInit resets all buttons to false.
   */
  readonly internalControl = new FormControl<
    ShiftStatusEnum | ShiftStatusEnum[] | null
  >(null);

  private _value: ShiftStatusEnum | ShiftStatusEnum[] | null = null;
  private _excludeStatuses: ShiftStatusEnum[] = [];
  private _internalSub?: Subscription;

  private _onChange = (value: ShiftStatusEnum | ShiftStatusEnum[] | null) => {
    // This is intentional - will be replaced by Angular forms
  };
  private _onTouched = () => {
    // This is intentional - will be replaced by Angular forms
  };

  ngOnInit(): void {
    this.initializeOptions();
    // Propagate button-group value changes to the outer Angular form.
    this._internalSub = this.internalControl.valueChanges.subscribe((value) => {
      this._value = value;
      this._onChange(value);
      this._onTouched();
      this.valueChange.emit(value);
      this.selectionChange.emit(value);
    });
  }

  ngOnDestroy(): void {
    this._internalSub?.unsubscribe();
  }

  private initializeOptions(): void {
    this.options = Object.entries(SHIFT_STATUS_RECORD)
      .filter(
        ([enumValue]) =>
          !this._excludeStatuses.includes(enumValue as ShiftStatusEnum)
      )
      .map(([enumValue, label]) => ({
        value: enumValue as ShiftStatusEnum,
        label: label
      }));
  }

  /** ControlValueAccessor implementation */
  writeValue(value: ShiftStatusEnum | ShiftStatusEnum[] | null): void {
    this._value = value;
    // Keep the internal control in sync so the button-group reflects saved values.
    this.internalControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(
    fn: (value: ShiftStatusEnum | ShiftStatusEnum[] | null) => void
  ): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onSelectionChange(value: ShiftStatusEnum | ShiftStatusEnum[] | null): void {
    this._value = value;
    this._onChange(value);
    this._onTouched();
    this.valueChange.emit(value);
    this.selectionChange.emit(value);
  }

  onFocus(): void {
    // Can be used for additional logic if needed
  }

  onBlur(): void {
    this._onTouched();
  }
}
